import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookie } from '@fortawesome/free-solid-svg-icons';

const CookiesConsent = () => {
  const { t } = useTranslation();
  // Read consent from localStorage on first render to avoid any flash of the banner
  function loadConsentInit(): { hasConsent: boolean; analytics: boolean } {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('cookie_consent') : null;
      if (!raw) return { hasConsent: false, analytics: false };
      const parsed = JSON.parse(raw) as { expiresAt?: number; analytics?: boolean };
      if (typeof parsed?.expiresAt === 'number' && parsed.expiresAt > Date.now()) {
        return { hasConsent: true, analytics: !!parsed.analytics };
      }
      // Expired -> clear and treat as no consent
      if (typeof window !== 'undefined') localStorage.removeItem('cookie_consent');
      return { hasConsent: false, analytics: false };
    } catch {
      return { hasConsent: false, analytics: false };
    }
  }
  const init = loadConsentInit();
  const [open, setOpen] = useState(() => !init.hasConsent);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState<boolean>(() => init.analytics);

  // Save consent in localStorage with an expiry time
  const CONSENT_KEY = 'cookie_consent';
  // Use 1 minute for testing; switch to 1 year in production.
  //const EXPIRY_MS = 60 * 1000; // 1 minute
  const EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 1 year (production)

  type Consent = {
    essential: true; // essential cookies are always on
    analytics: boolean; // user choice for analytics
    timestamp: number; // when the choice was saved
    expiresAt: number; // when the choice should be asked again
  };

  const saveConsent = (analyticsChoice: boolean) => {
    const now = Date.now();
    const payload: Consent = {
      essential: true,
      analytics: analyticsChoice,
      timestamp: now,
      expiresAt: now + EXPIRY_MS,
    };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    } catch {}
  };

  // No need for useEffect here; state is already correct on first render

  const handleAllowAll = () => {
    // Allow essential (always true) + analytics
    saveConsent(true);
    setOpen(false);
    setShowPrefs(false);
  };

  const handleDeclineAll = () => {
    // Keep essential (always true) and disable analytics
    saveConsent(false);
    setOpen(false);
    setShowPrefs(false);
  };

  const handleSavePreferences = () => {
    // Keep essential on; apply user analytics choice
    saveConsent(analytics);
    setOpen(false);
    setShowPrefs(false);
  };

  if (!open) return null;

  return (
    <div className="fixed bottom-4 left-0 right-0 z-50 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-[99vw] max-w-7xl md:w-[90vw] md:max-w-6xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-4 flex flex-col relative">
        <button
          className="absolute top-4 right-5 text-slate-400 hover:text-slate-700 dark:hover:text-white text-2xl font-bold focus:outline-none"
          aria-label="Close"
          onClick={() => setOpen(false)}
        >
          ×
        </button>
        {/* Icon, text, and buttons */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 w-full">
          <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md mb-2 md:mb-0">
            <FontAwesomeIcon icon={faCookie} className="text-2xl text-slate-700 dark:text-slate-200" />
          </div>
          <div className="flex-1 flex flex-col justify-center w-full">
            <div className="text-slate-900 dark:text-white text-base font-semibold mb-1">{t('cookiesConsent.title')}</div>
            <div className="text-slate-600 dark:text-slate-300 text-sm mb-2">{t('cookiesConsent.description')}</div>
            {!showPrefs ? (
              <div className="flex gap-2 flex-wrap justify-start w-full">
                <button
                  className="px-3 py-2 rounded-lg bg-slate-900 dark:bg-slate-700 text-white font-semibold text-sm hover:bg-slate-800 dark:hover:bg-slate-600 transition shadow"
                  onClick={handleAllowAll}
                >
                  {t('cookiesConsent.allowAll')}
                </button>
                <button
                  className="px-3 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white font-semibold text-sm hover:bg-slate-300 dark:hover:bg-slate-600 transition shadow"
                  onClick={handleDeclineAll}
                >
                  {t('cookiesConsent.decline')}
                </button>
                <button
                  className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition shadow"
                  onClick={() => setShowPrefs(true)}
                >
                  {t('cookiesConsent.preferences')}
                </button>
              </div>
            ) : (
              <>
                <div className="text-slate-900 dark:text-white text-base font-semibold mb-1">
                  {t('cookiesConsent.settingsTitle')}
                </div>
                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700 dark:text-slate-200">{t('cookiesConsent.essential')}</span>
                    <span className="text-xs text-slate-400">{t('cookiesConsent.alwaysEnabled')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700 dark:text-slate-200">{t('cookiesConsent.analytics')}</span>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-slate-900 dark:text-slate-200"
                        checked={analytics}
                        onChange={() => setAnalytics((v) => !v)}
                      />
                      <span className="ml-2 text-xs text-slate-600 dark:text-slate-300">{t('cookiesConsent.enable')}</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end md:justify-start">
                  <button
                    className="px-3 py-2 rounded-lg bg-slate-900 dark:bg-slate-700 text-white font-semibold text-sm hover:bg-slate-800 dark:hover:bg-slate-600 transition shadow"
                    onClick={handleSavePreferences}
                  >
                    {t('cookiesConsent.save')}
                  </button>
                  <button
                    className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition shadow"
                    onClick={() => setShowPrefs(false)}
                  >
                    {t('cookiesConsent.back')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

                export default CookiesConsent;
